import { useEffect, useState } from 'react'
import type { Model } from '../../../data/models'
import type { GammaHfPosition, GammaHfSlots } from '../utils/gammaOptions'
import type {
	CharacterOrientation,
	OutputFormat,
	SafetyFilter,
	Speed,
	VideoReferenceType,
} from '../utils/constants'
import {
	CHARACTER_ORIENTATION,
	GENERATE_SETTINGS_KEY,
	OUTPUT_FORMAT,
	SAFETY_FILTER,
	SPEED,
	VIDEO_REFERENCE_TYPE,
} from '../utils/constants'
import { effectiveOutputFormat, modelParams } from '../utils/modelParams'

// ── Presentation (Umnik Slides) ──
// Настройки презентационной генерации — под-namespace внутри settings,
// чтобы не плодить плоские ключи `presentationMode/presentationTemplate/…`
// и иметь один типизированный островок под категорию.
export type PresentationMode = 'edit' | 'create'

export type GammaImageSource =
	| 'aiGenerated'
	| 'webAllImages'
	| 'webFreeToUse'
	| 'pexels'
	| 'giphy'
	| 'pictographic'
	| 'themeAccent'
	| 'placeholder'
	| 'noImages'

export interface PresentationSettings {
	/** Режим работы: редактирование существующей PPTX или создание с нуля. */
	mode: PresentationMode
	/** ID встроенного шаблона из /presentations/templates (create mode). */
	template?: string
	/** URL загруженного своего PPTX-шаблона (перекрывает `template`). */
	customTemplateUrl?: string
	/** Число слайдов 1..15 (create mode). undefined = AI решит сам. */
	slideCount?: number
	/** URL загруженной PPTX которую будем редактировать (edit mode). */
	fileUrl?: string
	/** URLs прикреплённых картинок которые AI вставит в слайды. */
	imageUrls?: string[]
	/** Имена файлов параллельно imageUrls (для подписи AI в промпте). */
	imageFilenames?: string[]
	// ── Gamma-специфичные настройки (игнорируются для Umnik Slides) ──
	/** Количество слайдов для Gamma (1..50). */
	gammaNumCards?: number
	/** Язык генерации (ISO: ru | en | de | ...). */
	gammaLanguage?: string
	/** Источник изображений Gamma. */
	gammaImageSource?: GammaImageSource
	/** Формат слайда: широкоформат, классика и соцсетевые (квадрат/портрет/сторис). */
	gammaDimensions?: '16x9' | '4x3' | '1x1' | '4x5' | '9x16'
	/** ID темы из Gamma /themes (опционально). */
	gammaThemeId?: string
	/** Имя выбранной темы — для строк настроек и сводки («ставить везде, а не
	 *  молча определять», решение владельца 2026-08-13). В payload не уходит. */
	gammaThemeName?: string
	/** Дополнительные инструкции Gamma (опционально). */
	gammaAdditionalInstructions?: string
	/** Количество текста на слайде: brief | medium | detailed. */
	gammaTextAmount?: 'brief' | 'medium' | 'detailed' | 'extensive'
	/** Модель AI-картинок Gamma ('' = Gamma решает). Платные тиры дают
	 *  надбавку к цене за слайд (см. gammaOptions.ts). */
	gammaImageModel?: string
	/** Текстовое описание стиля AI-картинок (imageOptions.style, до 5000). */
	gammaImageStyle?: string
	/** Как трактовать промпт: generate | condense | preserve. */
	gammaTextMode?: 'generate' | 'condense' | 'preserve'
	/** Формат готового файла: pptx (правится в PowerPoint) | pdf | png. */
	gammaExportAs?: 'pptx' | 'pdf' | 'png'
	/** Тон презентации: пусто (auto) | formal | casual | persuasive | friendly |
	 *  inspirational | educational. Передаётся в Gamma textOptions.tone. */
	gammaTone?: string
	/** Целевая аудитория свободным текстом: «инвесторы B2B-сектора», «школьники
	 *  5-7 класс», и т.д. Передаётся в Gamma textOptions.audience. */
	gammaAudience?: string
	/** Колонтитулы: карта 6 слотов кромки (у каждого — свой текст, номер или
	 *  логотип). Источник правды с 2026-08-14; поля ниже (gammaFooterText/
	 *  Number/…Pos) — легаси одноэлементной версии, читаются конвертером
	 *  hfStateFromSettings и зануляются при первом сохранении шита. */
	gammaHfSlots?: GammaHfSlots
	gammaFooterText?: string
	gammaFooterTextPos?: GammaHfPosition
	gammaFooterNumber?: boolean
	gammaFooterNumberPos?: GammaHfPosition
	/** Логотип в колонтитуле: ключ файла в нашем хранилище (бэк подписывает
	 *  свежий presigned-URL в момент генерации — ссылка не протухает) +
	 *  presigned-URL для превью в шите (живёт ~1 час, только отображение). */
	gammaFooterLogoKey?: string
	gammaFooterLogoUrl?: string
	gammaFooterLogoPos?: GammaHfPosition
	gammaFooterLogoSize?: 'sm' | 'md' | 'lg' | 'xl'
	/** Не показывать колонтитулы на первом/последнем слайде (титул/финал). */
	gammaFooterHideFirst?: boolean
	gammaFooterHideLast?: boolean
}

export interface GenerateSettings {
	aspectRatio: string
	resolution: string
	version: string
	speed: Speed
	stylization: number
	duration: string
	sound: boolean
	characterOrientation: CharacterOrientation
	keepOriginalSound: boolean
	safetyFilter: SafetyFilter
	outputFormat: OutputFormat
	quality: string
	negativePrompt: string
	googleSearch: boolean
	webSearch?: boolean
	/** Seedance 2.5: «Черновик» — проба в 480p, из которой потом делается финал 1080p (backend: services/seedance_draft).
	 *  Уходит в запрос только когда сервер отдаёт черновики и нет видео/аудио-референса (utils/generatePayloadBuilders). */
	draft?: boolean
	nsfwChecker?: boolean
	seedanceMode?: 'frames' | 'references' | 'video'
	/** Seedance 2.5 с видео-референсом: 'edit' — «Переделать видео» (длина как у референса),
	 *  'new' — «Новое по референсу» (длительность выбирает юзер). См. utils/referenceLimits. */
	videoIntent?: 'edit' | 'new'
	/** У тренда есть видео (hidden — скрыто от пользователя, его подставит сервер; removed — человек снял его из слота).
	 *  Переделывается ли оно — общее правило сайта и админки (shared/trend-video → referenceLimits.trendVideoFollows);
	 *  ставит виджет тренда. */
	cardTrendVideo?: { hidden: boolean; removed?: boolean }
	/** Замер видео тренда (сервер): длина в секундах и кадр — для строки «Формат и длина — как у видео тренда». */
	cardReferenceSeconds?: number
	cardReferenceFrame?: { width: number; height: number }
	/** Длительность тренда под замком (карточка: settings.locked) — пилюля заперта, сервер другую длину не примет. */
	cardDurationLock?: number
	/** Какие настройки пользователь поменял сам (пилюли и шторка — userUpdateSetting); сбрасывается, когда применяются
	 *  настройки тренда. Уходит в запрос как user_changed — блок «Тренд» в админке (11.10). */
	userChanged?: string[]
	/** «Повторить движение» в генераторе (13.10): фото людей по слотам слева направо — в запрос вместо каналов виджета
	 *  (у замены человека фото обязательное, а каналов на несколько нет); ставит useMotionMode. */
	motionPhotos?: string[]
	/** Подсказка «кто кого заменяет» для P-Video (instruction_prompt Pruna) — replace_instruction запроса. */
	motionInstruction?: string
	promptOptimizer: boolean
	endImageUrl: string
	referenceImageUrls: string[]
	videoReferenceType: VideoReferenceType
	multiPrompt: string
	// ── Аудио (Suno) ──
	audioTitle: string
	audioStyle: string
	audioInstrumental: boolean
	audioVocalGender: string
	audioCustomMode: boolean
	/** Стили которых НЕ должно быть в песне. В API уходит как `negative_tags`. */
	audioNegativeTags?: string
	// ── Audio submode: 'music' (default) | 'sounds' (SFX режим) ──
	audioSubmode?: 'music' | 'sounds' | 'soundtrack' | 'tts'
	/** TTS (озвучка): voice_id голоса ElevenLabs. Пусто — голос по умолчанию (решает бэк). */
	audioVoice?: string
	/** Имя выбранного голоса — для подписи пилюли: голос библиотеки не из
	 *  фиксированного списка, по одному id имя не узнать. */
	audioVoiceName?: string
	/** Sounds: генерировать как loop (для зацикленных SFX типа «rain»). */
	soundLoop?: boolean
	/** Sounds: BPM для ритмических звуков (опционально). */
	soundTempo?: number
	/** Sounds: тональность (например 'C', 'Am'). Опционально. */
	soundKey?: string
	/** Extend: с какой секунды продолжить трек. 0 = с конца. */
	audioContinueAt?: number
	/** Саундтрек по видео: под какой кусок ролика писать музыку (секунды).
	 *  Пусто = весь ролик. Минимальный кусок — 3 секунды (правило Mureka). */
	soundtrackStartSec?: number
	soundtrackEndSec?: number
	/** «+ Инструментал» у Mureka: какую партию дописать к треку. Пусто =
	 *  инструментал целиком (как было). Иначе конкретный инструмент —
	 *  Drums | Bass | Guitar | Keyboard | Percussion | Strings | Synth | FX |
	 *  Brass | Woodwinds | Vocals. Suno это поле игнорирует. */
	audioTrackType?: string
	/** UUID персоны в suno_personas — генерировать с этим голосом. Опционально. */
	audioPersonaId?: string | null
	// ── Presentation (Umnik Slides) ──
	/** Под-namespace настроек презентации. Заполнен когда модель
	 *  category='presentation'. Для других категорий не используется. */
	presentation?: PresentationSettings

	// ── HeyGen (для моделей heygen/avatar-iv, heygen/video-agent,
	//    heygen/video-translate). Все поля опциональны — если не выставлены,
	//    backend / Replicate-модели используют свои дефолты. ─────────
	/** id выбранного аватара (из /api/v1/heygen/avatars). avatar-iv: required,
	 *  video-agent: optional (auto-pick если не задан). */
	heygenAvatarId?: string
	/** id выбранного голоса (из /api/v1/heygen/voices). avatar-iv: required. */
	heygenVoiceId?: string
	/** Стиль обрамления аватара: normal | closeUp | circle. avatar-iv only. */
	heygenAvatarStyle?: 'normal' | 'closeUp' | 'circle'
	/** Скорость TTS-голоса 0.5–1.5. avatar-iv only. */
	heygenVoiceSpeed?: number
	/** Эмоция голоса: Excited|Friendly|Serious|Soothing|Broadcaster. avatar-iv,
	 *  только для voices с emotion_support=true. */
	heygenVoiceEmotion?: string
	/** Burn-in субтитры на видео. avatar-iv only. */
	heygenCaption?: boolean
	/** URL исходного видео для перевода. video-translate only. Юзер
	 *  либо вставляет URL, либо загружает mp4 (URL подставляется
	 *  после upload в MinIO). */
	heygenVideoUrl?: string
	/** Целевой язык перевода (см. /api/v1/heygen/translation-languages).
	 *  video-translate only. */
	heygenOutputLanguage?: string
	/** Подгонять длительность под темп речи разных языков. video-translate. */
	heygenEnableDynamicDuration?: boolean
	/** Только аудио-дорожка, без lipsync. video-translate. */
	heygenTranslateAudioOnly?: boolean
	/** Метка для трекинга в HeyGen (опционально). video-translate. */
	heygenTitle?: string
}

const DEFAULT_SETTINGS: GenerateSettings = {
	aspectRatio: '1:1',
	resolution: '1K',
	version: '7',
	speed: SPEED.FAST,
	stylization: 100,
	duration: '5',
	sound: false,
	draft: false,
	characterOrientation: CHARACTER_ORIENTATION.VIDEO,
	keepOriginalSound: true,
	safetyFilter: SAFETY_FILTER.LOW,
	outputFormat: OUTPUT_FORMAT.JPG,
	quality: 'medium',
	negativePrompt: '',
	googleSearch: false,
	webSearch: false,
	nsfwChecker: true,
	seedanceMode: 'frames',
	videoIntent: 'edit',
	promptOptimizer: true,
	endImageUrl: '',
	referenceImageUrls: [],
	videoReferenceType: VIDEO_REFERENCE_TYPE.FEATURE,
	multiPrompt: '',
	// ── Аудио ──
	audioTitle: '',
	audioStyle: 'Pop',
	audioInstrumental: false,
	audioVocalGender: '',
	audioCustomMode: true,
	audioSubmode: 'music',
	audioVoice: '',
	audioVoiceName: '',
	soundLoop: false,
	presentation: { mode: 'create' },
}

/** Служебные ключи настроек — их ставит сам виджет, это не выбор человека. */
const INTERNAL_SETTING_KEYS: ReadonlySet<string> = new Set<keyof GenerateSettings>([
	'userChanged',
	'cardTrendVideo',
	'cardReferenceSeconds',
	'cardReferenceFrame',
	'cardDurationLock',
	'motionPhotos',
	'motionInstruction',
])

/** Настройка, которую поменял человек, → имя поля запроса (camelCase → snake_case, backend services/card_check).
 *  Любая: сервер сверяет с пресетом тренда все его настройки, новая попадает в сверку сама. null — служебный ключ. */
export function userSettingParam(key: string): string | null {
	if (INTERNAL_SETTING_KEYS.has(key)) return null
	return key.replace(/[A-Z]/g, (c) => `_${c.toLowerCase()}`)
}

function loadSavedSettings(): GenerateSettings {
	try {
		const saved = localStorage.getItem(GENERATE_SETTINGS_KEY)
		if (saved) {
			const parsed = JSON.parse(saved)
			delete parsed.cardTrendVideo
			delete parsed.cardReferenceSeconds
			delete parsed.cardReferenceFrame
			delete parsed.cardReferenceVideo
			delete parsed.cardDurationLock
			delete parsed.userChanged
			return { ...DEFAULT_SETTINGS, ...parsed }
		}
	} catch {
		// ignore
	}
	return DEFAULT_SETTINGS
}

export function useGenerateSettings(selectedModel: Model | null) {
	const [settings, setSettings] = useState<GenerateSettings>(loadSavedSettings)

	// Persist to localStorage
	useEffect(() => {
		localStorage.setItem(GENERATE_SETTINGS_KEY, JSON.stringify(settings))
	}, [settings])

	// Sync settings when model changes.
	// Resolution, duration, quality — ВСЕГДА сбрасываем к дефолту модели
	// (первый элемент массива = самый дешёвый вариант). Это гарантирует что
	// цена на /generate совпадает с ценой на карточке в каталоге (model.cost =
	// min_credits_price). Если юзер пришёл с home-page карточки с конкретными
	// settings — pendingSettings эффект в GenerateContext перезапишет поверх.
	//
	// Aspect ratio — НЕ сбрасываем если текущее значение совместимо: это
	// пользовательское предпочтение которое не влияет на цену.
	useEffect(() => {
		if (!selectedModel) return
		setSettings((prev) => {
			const updates: Partial<GenerateSettings> = {}
			if (selectedModel.durations) {
				updates.duration = selectedModel.durations[0]
			}
			if (selectedModel.resolutions) {
				updates.resolution = selectedModel.resolutions[0]
			}
			if (selectedModel.qualityLevels) {
				updates.quality = selectedModel.qualityLevels[0]
			}
			if (selectedModel.aspectRatios && !selectedModel.aspectRatios.includes(prev.aspectRatio)) {
				updates.aspectRatio = selectedModel.aspectRatios[0]
			}
			// Формат — как пропорции: совместимый оставляем, чужой приводим к варианту модели (jpg ↔ jpeg, иначе
			// первый). Только у моделей с пилюлей «Формат»: у остальных он не уходит в запрос, а выбор юзера
			// должен пережить заход в такую модель.
			if (modelParams(selectedModel).outputFormat) {
				const fmt = effectiveOutputFormat(selectedModel, prev.outputFormat)
				if (fmt && fmt !== prev.outputFormat) updates.outputFormat = fmt as OutputFormat
			}
			return Object.keys(updates).length > 0 ? { ...prev, ...updates } : prev
		})
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [selectedModel?.id])

	function resetSettings(model: Model): void {
		setSettings({
			...DEFAULT_SETTINGS,
			aspectRatio: model.aspectRatios?.[0] || '1:1',
			resolution: model.resolutions?.[0] || '768p',
			duration: model.durations?.[0] || '5',
		})
	}

	function updateSetting<K extends keyof GenerateSettings>(
		key: K,
		value: GenerateSettings[K],
	): void {
		setSettings((prev) => ({ ...prev, [key]: value }))
	}

	return { settings, setSettings, updateSetting, resetSettings }
}
